/**
 * Sites — lists the authenticated user's sites with status summaries.
 * Links to detail pages and includes a "Create New Site" button.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.ts';
import { StatusBadge } from '../components/StatusBadge.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import type { Site } from '../types.ts';

/** Format an ISO date string for display using the current i18n locale. */
const formatDate = (iso: string, locale: string): string => {
  try {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export const Sites = () => {
  const { t, i18n } = useTranslation();
  const {
    data: sitesResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['sites'],
    queryFn: () => api.sites.list(),
  });

  const sites: Site[] = sitesResponse?.data ?? [];

  if (isLoading) {
    return (
      <div className="loading-page">
        <div className="spinner spinner--lg" aria-label={t('sites.loading')} />
        <span>{t('sites.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert--error" role="alert">
        {t('sites.loadError', { error: error instanceof Error ? error.message : t('common.error') })}
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 className="page-header__title">{t('sites.title')}</h2>
          <p className="page-header__description">
            {t('sites.description')}
          </p>
        </div>
        <Link to="/sites/new" className="btn btn--primary">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t('sites.createButton')}
        </Link>
      </div>

      {sites.length === 0 ? (
        <EmptyState
          title={t('sites.empty.title')}
          description={t('sites.empty.description')}
          action={
            <Link to="/sites/new" className="btn btn--primary">
              {t('sites.empty.action')}
            </Link>
          }
        />
      ) : (
        <div className="site-grid">
          {sites.map((site) => (
            <div key={site.id} className="card site-card">
              <Link to={`/sites/${site.id}`} className="site-card__link">
                <div className="card__body">
                  <div className="site-card__name">{site.subdomain}</div>
                  <div className="site-card__subdomain">
                    {site.subdomain}.refineweb.site
                  </div>
                  <div className="site-card__meta">
                    <span>{t('sites.theme', { themeId: site.themeId })}</span>
                    <span>{formatDate(site.createdAt, i18n.language)}</span>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
